import { useCallback, useState } from 'react'
import type { UserAvatar, UserPreferredLanguage } from '@/graphql/schema'
import type { SupportedLanguage } from '@/i18n/language'
import { UserPreferredLanguage as UserPreferredLanguageEnum } from '@/graphql/schema'
import { resolveLanguage } from '@/i18n/language'
import { useUpdateCurrentUserMutation } from '@/graphql/mutations/updateCurrentUserMutation.gql.generated'
import { CurrentUserDocument } from '@/graphql/queries/currentUserQuery.gql.generated'
import { resolveActionError } from '@/shared/lib/action-error'

export function normalizeProfileName(value: string | null | undefined) {
  const trimmed = value?.trim()

  return trimmed ? trimmed : null
}

export function uiLanguageToApiEnum(
  language: SupportedLanguage,
): UserPreferredLanguage {
  switch (language) {
    case 'ru':
      return UserPreferredLanguageEnum.Ru
    case 'es':
      return UserPreferredLanguageEnum.Es
    default:
      return UserPreferredLanguageEnum.En
  }
}

type Notify = (notification: {
  title: string
  description: string
  variant: 'error'
}) => void

export function useUpdateCurrentUserProfile() {
  return useUpdateCurrentUserMutation({
    awaitRefetchQueries: true,
    refetchQueries: [{ query: CurrentUserDocument }],
  })
}

export function useInstantProfileUpdate({
  apiLanguage,
  changeI18nLanguage,
  currentAvatar,
  notify,
}: {
  apiLanguage: UserPreferredLanguage | null | undefined
  changeI18nLanguage: (language: SupportedLanguage) => void
  currentAvatar: UserAvatar | null
  notify: Notify
}) {
  const [updateCurrentUser, result] = useUpdateCurrentUserProfile()
  const [pendingAvatar, setPendingAvatar] = useState<UserAvatar | null>(null)
  const [pendingLanguage, setPendingLanguage] =
    useState<SupportedLanguage | null>(null)
  const resolvedLanguage = resolveLanguage({
    apiLanguage,
    browserLocale: typeof navigator !== 'undefined' ? navigator.language : 'en',
  })
  const selectedAvatar = pendingAvatar ?? currentAvatar
  const selectedLanguage = pendingLanguage ?? resolvedLanguage

  const notifyFailure = useCallback(
    (error: unknown) => {
      const actionError = resolveActionError(error, 'Please try again.')

      notify({
        title: 'Profile update failed',
        description: actionError.message,
        variant: 'error',
      })
    },
    [notify],
  )

  const changeAvatar = useCallback(
    async (avatar: UserAvatar) => {
      if (avatar === selectedAvatar) return
      setPendingAvatar(avatar)
      try {
        await updateCurrentUser({ variables: { input: { avatar } } })
      } catch (error) {
        notifyFailure(error)
      } finally {
        setPendingAvatar(null)
      }
    },
    [notifyFailure, selectedAvatar, updateCurrentUser],
  )

  const changeLanguage = useCallback(
    async (language: SupportedLanguage) => {
      if (language === selectedLanguage) return
      setPendingLanguage(language)
      try {
        await updateCurrentUser({
          variables: {
            input: { preferredLanguage: uiLanguageToApiEnum(language) },
          },
        })
        changeI18nLanguage(language)
      } catch (error) {
        notifyFailure(error)
      } finally {
        setPendingLanguage(null)
      }
    },
    [changeI18nLanguage, notifyFailure, selectedLanguage, updateCurrentUser],
  )

  return {
    changeAvatar,
    changeLanguage,
    isSaving: result.loading,
    selectedAvatar,
    selectedLanguage,
  }
}

export function useAppSheetProfile(
  currentUser: {
    name?: string | null
    avatar?: UserAvatar | null
    preferredLanguage?: UserPreferredLanguage | null
  } | null,
) {
  const apiName = normalizeProfileName(currentUser?.name)
  const currentAvatar = currentUser?.avatar ?? null
  const apiLanguage = currentUser?.preferredLanguage ?? null

  return {
    apiLanguage,
    apiName,
    currentAvatar,
  }
}
